// 从localStorage获取token
const getStoredToken = () => {
  return localStorage.getItem('token') || null;
};

// 创建一个变量来存储 API 实例的 getter 函数
let getApiInstance = null;

// 提供设置 API 实例 getter 的方法
export function setApiGetter(getter) {
  getApiInstance = getter;
}

// 获取当前 API 实例的辅助函数
function getApi() {
  if (!getApiInstance) {
    throw new Error('API getter not set. Please initialize the auth module with an API instance.');
  }
  const api = getApiInstance();
  if (!api || !api.authApi) {
    throw new Error('Invalid API instance. Make sure the API object has authApi property.');
  }
  return api;
}

// 从localStorage获取用户信息
const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      return null;
    }
    return JSON.parse(userStr);
  } catch (e) {
    console.error('Failed to parse user data:', e);
    return null;
  }
};

const state = {
  token: getStoredToken(),
  user: (() => {
    try {
      return getStoredUser();
    } catch (e) {
      console.error('Failed to initialize user state:', e);
      return null;
    }
  })(),
  loading: false,
  error: null,
  wechatSignatures: {
    corp: null,
    agent: null
  },
  wechatUsers: [] // 企业微信用户列表状态
};

const getters = {
  isAuthenticated: state => !!state.token,
  currentUser: state => state.user,
  authLoading: state => state.loading,
  authError: state => state.error,
  wechatUsers: state => state.wechatUsers,
  // 获取特定类型的微信签名
  wechatSignature: (state) => (type) => state.wechatSignatures[type] || null
};

const actions = {
  /**
   * 获取微信签名
   * @param {Object} context - Vuex上下文
   * @param {Object} payload - 请求参数
   * @param {string} payload.url - 当前页面URL
   * @param {string} payload.type - 签名类型 (corp|agent)
   */
  async wechatSignature({ commit }, { url, type }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例
      const api = getApi();

      // 调用API服务获取微信签名
      const response = await api.wechatAuthApi.wechatSignature({ url, type });
      
      // 根据实际响应结构调整解构
      const { timestamp, nonceStr, signature } = response.data;
      
      // 存储签名信息
      commit('setWechatSignature', { 
        type, 
        signature: { timestamp, nonceStr, signature } 
      });
      
      return { timestamp, nonceStr, signature };
    } catch (error) {
      console.error('微信签名获取错误:', error);
      commit('setError', error.response?.data?.message || error.message || '微信签名获取失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  /**
   * 获取企业微信当前用户信息
   * @param {Object} context - Vuex上下文
   */
  async wechatFetchCurrentUser({ commit }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例
      const api = getApi();

      const response = await api.wechatAuthApi.wechatFetchCurrentUser();
      const wechatUser = response.data;
      
      // 存储企业微信用户信息
      commit('setWechatUser', wechatUser);
      
      return wechatUser;
    } catch (error) {
      console.error('获取企业微信用户信息错误:', error);
      commit('setError', error.response?.data?.message || error.message || '获取企业微信用户信息失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  /**
   * 获取企业微信用户列表
   * @param {Object} context - Vuex上下文
   */
  async wechatGetUsers({ commit }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例
      const api = getApi();

      const response = await api.wechatAuthApi.wechatGetUsers();
      const users = response.data.users;
      
      commit('setWechatUsers', users);
      
      return users;
    } catch (error) {
      console.error('获取企业微信用户列表错误:', error);
      commit('setError', error.response?.data?.message || error.message || '获取企业微信用户列表失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  /**
   * 搜索企业微信用户
   * @param {Object} context - Vuex上下文
   * @param {string} keyword - 搜索关键词
   */
  async wechatSearchUser({ commit }, keyword) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例
      const api = getApi();

      const response = await api.wechatAuthApi.wechatSearchUser(keyword);
      const users = response.data.users;
      
      return users;
    } catch (error) {
      console.error('搜索企业微信用户错误:', error);
      commit('setError', error.response?.data?.message || error.message || '搜索企业微信用户失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
};

const mutations = {
  setAuth(state, { token, user }) {
    state.token = token;
    state.user = user;
  },
  
  setUser(state, user) {
    state.user = user;
  },
  
  clearAuth(state) {
    state.token = null;
    state.user = null;
  },
  
  setLoading(state, status) {
    state.loading = status;
  },
  
  setError(state, error) {
    state.error = error;
  },
  
  clearError(state) {
    state.error = null;
  },
  
  /**
   * 设置微信签名
   * @param {Object} state - Vuex状态
   * @param {Object} payload - 签名数据
   * @param {string} payload.type - 签名类型
   * @param {Object} payload.signature - 签名信息
   */
  setWechatSignature(state, { type, signature }) {
    state.wechatSignatures[type] = signature;
  },
  
  /**
   * 设置企业微信用户信息
   * @param {Object} state - Vuex状态
   * @param {Object} wechatUser - 企业微信用户信息
   */
  setWechatUser(state, wechatUser) {
    // 将企业微信信息合并到用户对象中
    if (state.user) {
      state.user = { 
        ...state.user, 
        wechat_info: wechatUser,
        wechat_userid: wechatUser.userid // 专门存储企业微信用户ID
      };
    }
  },
  
  /**
   * 设置企业微信用户列表
   * @param {Object} state - Vuex状态
   * @param {Array} users - 用户列表
   */
  setWechatUsers(state, users) {
    state.wechatUsers = users;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};